/**
 * Environment Configuration Utility
 *
 * Centralized environment detection and configuration management.
 * Eliminates code duplication across components and ensures consistent
 * environment-aware URL and key selection.
 */

interface EnvironmentConfig {
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
  environment: string;
  backend: {
    functionsUrl: string;
  };
  pythonBackend: {
    url: string;
  };
  stripe: {
    publishableKey: string;
  };
  app: {
    emailDiscountCoupon: string;
  };
  analytics: {
    enabled: boolean;
    measurementId?: string;
  };
  mixpanel: {
    enabled: boolean;
    token?: string;
  };
  pixels?: {
    facebookPixelId?: string;
    googleAdsConversionId?: string;
    googleAdsConversionLabel?: string;
  };
  firebase?: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId: string;
  };
}

/**
 * Get current environment configuration
 * Uses VITE_ENVIRONMENT instead of MODE for reliable production detection
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const environment = import.meta.env['VITE_ENVIRONMENT'] || 'development';
  const isProduction = environment === 'production';
  const isStaging = environment === 'staging';
  const isDevelopment = !isProduction && !isStaging;

  // Python FastAPI backend configuration
  const pythonBackendUrl = (import.meta.env['VITE_PYTHON_BACKEND_URL'] as string | undefined) || 'http://localhost:8000';

  // Python backend uses /api/v1 prefix
  const functionsUrl = `${pythonBackendUrl}/api/v1`;

  // Stripe configuration - uses standardized variable names
  const stripePublishableKey = import.meta.env['VITE_STRIPE_PUBLISHABLE_KEY'];

  // App configuration
  const emailDiscountCoupon = import.meta.env['VITE_EMAIL_DISCOUNT_COUPON'] || 'EMAIL_SIGNUP_20';

  // Analytics configuration - uses standardized variable names
  const gaMeasurementId = import.meta.env['VITE_GA_MEASUREMENT_ID'];

  // Enable analytics if ID exists. In development, require explicit opt-in via VITE_ENABLE_ANALYTICS_DEV="true"
  const analyticsEnabled = Boolean(gaMeasurementId) && (isProduction || isStaging || import.meta.env['VITE_ENABLE_ANALYTICS_DEV'] === 'true');

  // Mixpanel configuration - uses standardized variable names
  const mixpanelToken = import.meta.env['VITE_MIXPANEL_TOKEN'];

  // Enable Mixpanel if token exists. In development, require explicit opt-in via VITE_ENABLE_MIXPANEL_DEV="true"
  const mixpanelEnabled = Boolean(mixpanelToken) && (isProduction || isStaging || import.meta.env['VITE_ENABLE_MIXPANEL_DEV'] === 'true');

  // Conversion pixels configuration (optional)
  const facebookPixelId = import.meta.env['VITE_FACEBOOK_PIXEL_ID'] as string | undefined;
  const googleAdsConversionId = import.meta.env['VITE_GOOGLE_ADS_CONVERSION_ID'] as string | undefined;
  const googleAdsConversionLabel = import.meta.env['VITE_GOOGLE_ADS_CONVERSION_LABEL'] as string | undefined;

  // Firebase configuration (required for authentication)
  const firebaseApiKey = import.meta.env['VITE_FIREBASE_API_KEY'];
  const firebaseAuthDomain = import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'];
  const firebaseProjectId = import.meta.env['VITE_FIREBASE_PROJECT_ID'];
  const firebaseAppId = import.meta.env['VITE_FIREBASE_APP_ID'];

  // Validation
  if (!pythonBackendUrl) {
    throw new Error(
      `Missing Python backend configuration for ${environment} environment. ` +
        `Expected: VITE_PYTHON_BACKEND_URL`
    );
  }

  // Firebase config is required for authentication
  if (!firebaseApiKey || !firebaseAuthDomain || !firebaseProjectId || !firebaseAppId) {
    throw new Error(
      `Missing Firebase configuration for ${environment} environment. ` +
        `Expected: VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_APP_ID`
    );
  }

  if (!stripePublishableKey) {
    throw new Error(
      `Missing Stripe configuration for ${environment} environment. ` +
      `Expected: VITE_STRIPE_PUBLISHABLE_KEY`
    );
  }

  return {
    isDevelopment,
    isStaging,
    isProduction,
    environment,
    backend: {
      functionsUrl: functionsUrl,
    },
    pythonBackend: {
      url: pythonBackendUrl,
    },
    stripe: {
      publishableKey: stripePublishableKey,
    },
    app: {
      emailDiscountCoupon: emailDiscountCoupon,
    },
    analytics: {
      enabled: analyticsEnabled,
      ...(gaMeasurementId && { measurementId: gaMeasurementId }),
    },
    mixpanel: {
      enabled: mixpanelEnabled,
      ...(mixpanelToken && { token: mixpanelToken }),
    },
    ...((facebookPixelId || googleAdsConversionId) && {
      pixels: {
        ...(facebookPixelId && { facebookPixelId }),
        ...(googleAdsConversionId && { googleAdsConversionId }),
        ...(googleAdsConversionLabel && { googleAdsConversionLabel }),
      },
    }),
    firebase: {
      apiKey: firebaseApiKey,
      authDomain: firebaseAuthDomain,
      projectId: firebaseProjectId,
      appId: firebaseAppId,
    },
  };
}

/**
 * Convenience helpers for common use cases
 * These read import.meta.env directly to support test stubbing
 */
export const env = getEnvironmentConfig();

export const getFunctionsUrl = (): string => getEnvironmentConfig().backend.functionsUrl;
export const getPythonBackendUrl = (): string => getEnvironmentConfig().pythonBackend.url;
export const isDevelopment = (): boolean => getEnvironmentConfig().isDevelopment;
export const isStaging = (): boolean => getEnvironmentConfig().isStaging;
export const isProduction = (): boolean => getEnvironmentConfig().isProduction;
/**
 * Get the correct base URL for the current environment
 * Fixes production localhost redirect issues
 */
export const getBaseUrl = (): string => {
  const config = getEnvironmentConfig();
  if (config.isProduction) {
    return 'https://tangi.ai';
  }

  if (config.isStaging) {
    return 'https://staging.tangi.ai';
  }

  // For development, use window.location.origin if available
  const devUrl = typeof window !== 'undefined' ? globalThis.location.origin : 'http://localhost:5173';
  return devUrl;
};

// ============================================================================
// API URL HELPERS - Centralized URL building for API clients (DRY)
// ============================================================================

/**
 * Get API endpoint URL for Python backend
 *
 * @param endpoint - Endpoint path (e.g., 'orders/by-session', 'admin/statistics')
 * @returns Full URL with base and /api/v1 prefix
 * @throws Error if Python backend URL is not configured
 *
 * @example
 * getApiEndpoint('orders/by-session') // => 'http://localhost:8000/api/v1/orders/by-session'
 * getApiEndpoint('admin/statistics')  // => 'http://localhost:8000/api/v1/admin/statistics'
 */
export function getApiEndpoint(endpoint: string): string {
  const pythonBackendUrl = getPythonBackendUrl();
  if (!pythonBackendUrl) {
    throw new Error('Python backend URL not configured');
  }
  // Normalize: remove leading slash from endpoint if present
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${pythonBackendUrl}/api/v1/${normalizedEndpoint}`;
}

/**
 * Get admin API endpoint URL
 *
 * @param endpoint - Admin endpoint path (e.g., 'statistics', 'orders')
 * @returns Full URL with admin prefix
 *
 * @example
 * getAdminApiEndpoint('statistics') // => 'http://localhost:8000/api/v1/admin/statistics'
 */
export function getAdminApiEndpoint(endpoint: string): string {
  return getApiEndpoint(`admin/${endpoint}`);
}
